import React from "react"
import { useState } from "react"
import { Card, Container, Row, Col, Form, Button, Alert } from "react-bootstrap"
import { FaUser, FaLock, FaEye, FaEyeSlash, FaUserMd, FaShieldAlt } from "react-icons/fa"
import { Link, useNavigate } from "react-router-dom"
import { useAuth } from "../../context/AuthContext"

export default function PatientLogin() {
  const [showPassword, setShowPassword] = useState(false)
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    rememberMe: false,
  })
  const [showAlert, setShowAlert] = useState({ show: false, variant: "", message: "" })
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const { login } = useAuth()

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.email || !formData.password) {
      setShowAlert({ show: true, variant: "danger", message: "Please enter valid credentials." })
      setTimeout(() => setShowAlert({ show: false, variant: "", message: "" }), 3000)
      return
    }

    setLoading(true)
    try {
      const result = await login(formData.email, formData.password)
      
      if (result.success) {
        setShowAlert({ show: true, variant: "success", message: "Login Successful! Welcome to your patient portal." })
        setTimeout(() => {
          navigate("/patient/dashboard")
        }, 1500)
      } else {
        setShowAlert({ show: true, variant: "danger", message: result.message })
      }
    } catch (error) {
      setShowAlert({ show: true, variant: "danger", message: "Login failed. Please try again." })
    } finally {
      setLoading(false)
      setTimeout(() => setShowAlert({ show: false, variant: "", message: "" }), 3000)
    }
  }

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  return (
    <div
      className="min-vh-100 d-flex align-items-center"
      style={{
        background: "linear-gradient(135deg, #0891b2 0%, #0e7490 100%)",
      }}
    >
      {/* Animated Background Elements */}
      <div className="position-absolute w-100 h-100">
        <div
          className="position-absolute rounded-circle opacity-10"
          style={{
            width: "300px",
            height: "300px",
            background: "white",
            top: "10%",
            right: "10%",
            animation: "float 6s ease-in-out infinite",
          }}
        ></div>
        <div
          className="position-absolute rounded-circle opacity-10"
          style={{
            width: "200px",
            height: "200px",
            background: "white",
            bottom: "20%",
            left: "5%",
            animation: "float 8s ease-in-out infinite reverse",
          }}
        ></div>
      </div>

      <Container className="position-relative">
        <Row className="justify-content-center">
          <Col md={6} lg={5} xl={4}>
            {showAlert.show && (
              <Alert
                variant={showAlert.variant}
                className="border-0 rounded-4 mb-4"
                style={{
                  backgroundColor: showAlert.variant === "success" 
                    ? "rgba(5, 150, 105, 0.1)" 
                    : "rgba(220, 38, 38, 0.1)",
                  border: showAlert.variant === "success" 
                    ? "1px solid rgba(5, 150, 105, 0.2)" 
                    : "1px solid rgba(220, 38, 38, 0.2)",
                }}
              >
                <div className="d-flex align-items-center">
                  <div
                    className="rounded-circle me-3 d-flex align-items-center justify-content-center"
                    style={{
                      width: "40px",
                      height: "40px",
                      background: showAlert.variant === "success"
                        ? "linear-gradient(135deg, #059669 0%, #047857 100%)"
                        : "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
                    }}
                  >
                    <FaShieldAlt className="text-white" size={16} />
                  </div>
                  <div>
                    <strong style={{ color: showAlert.variant === "success" ? "#059669" : "#dc2626" }}>
                      {showAlert.variant === "success" ? "Login Successful!" : "Login Failed"}
                    </strong>
                    <div style={{ color: showAlert.variant === "success" ? "#059669" : "#dc2626" }}>
                      {showAlert.message}
                    </div>
                  </div>
                </div>
              </Alert>
            )}

            <Card
              className="border-0 rounded-4 overflow-hidden"
              style={{
                boxShadow: "0 25px 80px rgba(0, 0, 0, 0.15)",
                backdropFilter: "blur(20px)",
                backgroundColor: "#f0fdfa",
              }}
            >
              {/* Header */}
              <div
                className="text-center p-5 text-white"
                style={{
                  background: "linear-gradient(135deg, #0891b2 0%, #0e7490 100%)",
                }}
              >
                <div
                  className="rounded-circle mx-auto mb-4 d-flex align-items-center justify-content-center"
                  style={{
                    width: "80px",
                    height: "80px",
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    backdropFilter: "blur(20px)",
                  }}
                >
                  <FaUserMd size={32} />
                </div>
                <h2 className="h3 fw-bold mb-2">Patient Portal</h2>
                <p className="mb-0 opacity-90">Access your medical records and appointments</p>
              </div>

              <Card.Body className="p-5">
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-medium mb-2" style={{ color: "#1f2937" }}>
                      Email Address
                    </Form.Label>
                    <div className="position-relative">
                      <div
                        className="position-absolute top-50 start-0 translate-middle-y ms-3"
                        style={{ zIndex: 2, color: "#6b7280" }}
                      >
                        <FaUser size={16} />
                      </div>
                      <Form.Control
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange("email", e.target.value)}
                        placeholder="Enter your email"
                        className="ps-5 py-3 border-0 rounded-3"
                        style={{
                          backgroundColor: "#ecfdf5",
                          transition: "all 0.3s ease",
                        }}
                        required
                        onFocus={(e) => {
                          e.target.style.backgroundColor = "#d1fae5"
                          e.target.style.boxShadow = "0 0 0 3px rgba(8, 145, 178, 0.1)"
                        }}
                        onBlur={(e) => {
                          e.target.style.backgroundColor = "#ecfdf5"
                          e.target.style.boxShadow = "none"
                        }}
                      />
                    </div>
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label className="fw-medium mb-2" style={{ color: "#1f2937" }}>
                      Password
                    </Form.Label>
                    <div className="position-relative">
                      <div
                        className="position-absolute top-50 start-0 translate-middle-y ms-3"
                        style={{ zIndex: 2, color: "#6b7280" }}
                      >
                        <FaLock size={16} />
                      </div>
                      <Form.Control
                        type={showPassword ? "text" : "password"}
                        value={formData.password}
                        onChange={(e) => handleInputChange("password", e.target.value)}
                        placeholder="Enter your password"
                        className="ps-5 pe-5 py-3 border-0 rounded-3"
                        style={{
                          backgroundColor: "#ecfdf5",
                          transition: "all 0.3s ease",
                        }}
                        required
                        onFocus={(e) => {
                          e.target.style.backgroundColor = "#d1fae5"
                          e.target.style.boxShadow = "0 0 0 3px rgba(8, 145, 178, 0.1)"
                        }}
                        onBlur={(e) => {
                          e.target.style.backgroundColor = "#ecfdf5"
                          e.target.style.boxShadow = "none"
                        }}
                      />
                      <Button
                        variant="link"
                        className="position-absolute top-50 end-0 translate-middle-y me-3 p-0 border-0"
                        style={{ zIndex: 2, color: "#6b7280" }}
                        onClick={() => setShowPassword(!showPassword)}
                        type="button"
                      >
                        {showPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
                      </Button>
                    </div>
                  </Form.Group>

                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <Form.Check
                      type="checkbox"
                      id="rememberMe"
                      label="Remember me"
                      checked={formData.rememberMe}
                      onChange={(e) => handleInputChange("rememberMe", e.target.checked)}
                      className="text-muted"
                    />
                    <Link to="/forgot-password" className="text-decoration-none fw-medium" style={{ color: "#0891b2" }}>
                      Forgot Password?
                    </Link>
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    disabled={loading}
                    className="w-100 py-3 rounded-3 fw-bold border-0 mb-4"
                    style={{
                      background: "linear-gradient(135deg, #0891b2 0%, #0e7490 100%)",
                      boxShadow: "0 8px 25px rgba(8, 145, 178, 0.4)",
                      transform: "translateY(0)",
                      transition: "all 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                      if (!loading) {
                        e.target.style.transform = "translateY(-2px)"
                        e.target.style.boxShadow = "0 12px 35px rgba(8, 145, 178, 0.5)"
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = "translateY(0)"
                      e.target.style.boxShadow = "0 8px 25px rgba(8, 145, 178, 0.4)"
                    }}
                  >
                    {loading ? "Signing In..." : "Sign In to Portal"}
                  </Button>

                  <div className="text-center">
                    <p className="text-muted mb-3">Don't have an account?</p>
                    <Link to="/patient/register">
                      <Button
                        variant="outline-primary"
                        className="px-4 py-2 rounded-3 fw-medium"
                        style={{
                          borderColor: "#0891b2",
                          color: "#0891b2",
                          transition: "all 0.3s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = "#0891b2"
                          e.target.style.color = "white"
                          e.target.style.transform = "translateY(-2px)"
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = "transparent"
                          e.target.style.color = "#0891b2"
                          e.target.style.transform = "translateY(0)"
                        }}
                      >
                        Create New Account
                      </Button>
                    </Link>
                  </div>
                </Form>
              </Card.Body>

              {/* Footer */}
              <div
                className="text-center p-4 text-white"
                style={{
                  background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
                }}
              >
                <div className="d-flex align-items-center justify-content-center mb-2">
                  <FaShieldAlt className="me-2" />
                  <span className="fw-medium">Secure & HIPAA Compliant</span>
                </div>
                <small className="opacity-90">
                  Your health information is protected with industry-standard security
                </small>
              </div>
            </Card>

            {/* Quick Links */}
            <div className="text-center mt-4">
              <div className="d-flex justify-content-center gap-4">
                <Link to="/" className="text-white text-decoration-none opacity-75 hover-opacity-100">
                  <small>← Back to Home</small>
                </Link>
                <Link to="/admin/login" className="text-white text-decoration-none opacity-75 hover-opacity-100">
                  <small>Admin Portal →</small>
                </Link>
              </div>
            </div>
          </Col>
        </Row>
      </Container>

      <style jsx>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }
        
        .hover-opacity-100:hover {
          opacity: 1 !important;
        }
      `}</style>
    </div>
  )
}
